// src/components/hr/HrMenteeAlertSettings.js
import React, { useState, useEffect } from 'react';

// Optional: Wenn du ein zentrales Layout / Wrapper für HR-Seiten hast,
// kannst du den später hier importieren. Für den Start bleibt es „nackt“.

const HrMenteeAlertSettings = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [testSending, setTestSending] = useState(false);
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    let alive = true;

    const loadRecipients = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(
          '/mentoring/notifications/mentee-alerts/recipients',
          { credentials: 'include' }
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || 'Fehler beim Laden der Empfänger');
        }

        if (!alive) return;

        setRecipients(data.recipients || []);
      } catch (err) {
        console.error('Fehler beim Laden der Mentee-Alert-Empfänger:', err);
        if (alive) {
          setError(err.message || 'Fehler beim Laden der Empfänger');
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadRecipients();

    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async (userId) => {
    const current = recipients.find((r) => r.user_id === userId);
    if (!current) return;

    const newValue = !current.receive_mentee_alerts;

    // Optimistisches Update im UI
    setRecipients((prev) =>
      prev.map((r) =>
        r.user_id === userId ? { ...r, receive_mentee_alerts: newValue } : r
      )
    );

    try {
      const res = await fetch(
        `/mentoring/notifications/mentee-alerts/recipients/${userId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            receive_mentee_alerts: newValue
          })
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success !== true) {
        throw new Error(data.error || 'Fehler beim Speichern der Einstellung');
      }
    } catch (err) {
      console.error('Fehler beim Speichern der Alert-Einstellung:', err);

      // Revert bei Fehler
      setRecipients((prev) =>
        prev.map((r) =>
          r.user_id === userId
            ? { ...r, receive_mentee_alerts: current.receive_mentee_alerts }
            : r
        )
      );

      alert(
        'Die Einstellung konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.'
      );
    }
  };

  const hasActiveRecipients = recipients.some((r) => r.receive_mentee_alerts);

  const handleSendTestEmail = async () => {
    if (!hasActiveRecipients || testSending) return;

    setTestMessage('');
    setTestSending(true);

    try {
      const res = await fetch(
        '/mentoring/notifications/mentee-alerts/test',
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success !== true) {
        throw new Error(
          data.error || 'Fehler beim Senden der Test-E-Mail'
        );
      }

      const sent = Number.isFinite(data.sent) ? data.sent : 0;
      const failed = Array.isArray(data.failed) ? data.failed.length : 0;

      let msg = '';
      if (sent === 0 && failed === 0) {
        msg = 'Es wurde keine Test-E-Mail versendet.';
      } else if (sent === 1) {
        msg = '1 Test-E-Mail wurde erfolgreich versendet.';
      } else {
        msg = `${sent} Test-E-Mails wurden erfolgreich versendet.`;
      }

      if (failed > 0) {
        msg += ` (${failed} Empfänger konnten nicht erreicht werden.)`;
      }

      setTestMessage(msg);
    } catch (err) {
      console.error('Fehler beim Senden der Test-E-Mail:', err);
      setTestMessage(
        err.message || 'Fehler beim Senden der Test-E-Mail.'
      );
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div className="hr-alert-settings-page" style={styles.page}>
      {/* Header-Bereich */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Benachrichtigungen &amp; Alerts</h1>
          <p style={styles.subtitle}>
            Legen Sie fest, welche HR-Personen kritische Mentee-Feedbacks per E-Mail erhalten.
          </p>
        </div>
      </header>

      <main style={styles.main}>
        {/* Card: Kritische Mentee-Zufriedenheit */}
        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div>
              <h2 style={styles.cardTitle}>
                Kritische Mentee-Zufriedenheit (≤ 3)
              </h2>
              <p style={styles.cardSubtitle}>
                Wählen Sie, welche HR-Personen benachrichtigt werden, wenn Mentees eine
                Zufriedenheit von 3 oder schlechter angeben.
              </p>
            </div>
          </div>

          <div style={styles.tableWrapper}>
            {loading && (
              <div style={styles.infoBox}>
                Daten werden geladen …
              </div>
            )}
            {error && (
              <div style={styles.errorBox}>
                {error}
              </div>
            )}

            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>E-Mail</th>
                  <th style={styles.thCenter}>Alerts erhalten</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r) => {
                  const name =
                    (r.first_name || r.last_name)
                      ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                      : r.email;

                  return (
                    <tr key={r.user_id} style={styles.tr}>
                      <td style={styles.td}>{name}</td>
                      <td style={styles.td}>{r.email}</td>
                      <td style={styles.tdCenter}>
                        <label style={styles.switchLabel}>
                          <input
                            type="checkbox"
                            checked={!!r.receive_mentee_alerts}
                            onChange={() => handleToggle(r.user_id)}
                            style={styles.switchInput}
                          />
                          <span
                            style={{
                              ...styles.switchSlider,
                              ...(r.receive_mentee_alerts
                                ? styles.switchSliderOn
                                : {})
                            }}
                          />
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {recipients.length > 0 &&
              recipients.every((r) => !r.receive_mentee_alerts) && (
                <div style={styles.warningBox}>
                  <strong style={styles.warningTitle}>
                    Hinweis:
                  </strong>{' '}
                  Aktuell ist keine HR-Person für kritische Mentee-Alerts aktiviert.
                  Es werden keine E-Mail-Benachrichtigungen verschickt.
                </div>
              )}
          </div>
        </section>

        {/* Card: Test-E-Mail */}
        <section style={styles.smallCard}>
          <h3 style={styles.cardTitle}>Test-E-Mail</h3>
          <p style={styles.cardSubtitle}>
            Senden Sie eine Test-Benachrichtigung an alle aktiven Empfänger, um die Konfiguration zu überprüfen.
          </p>
          <button
            type="button"
            style={
              hasActiveRecipients && !testSending
                ? styles.primaryButton
                : styles.primaryButtonDisabled
            }
            disabled={!hasActiveRecipients || testSending}
            onClick={handleSendTestEmail}
          >
            {testSending ? 'Sende Test-E-Mail …' : 'Test-E-Mail senden'}
          </button>
          {testMessage && (
            <p style={styles.testStatus}>{testMessage}</p>
          )}
          {!hasActiveRecipients && (
            <p style={styles.testHint}>
              Aktivieren Sie mindestens eine HR-Person, um eine Test-E-Mail zu senden.
            </p>
          )}
        </section>
      </main>
    </div>
  );
};

// 💎 Inline-Styles nur als Start, du kannst später auf deine CSS- oder Design-Utilitys umziehen
const styles = {
  page: {
    minHeight: '100vh',
    padding: '32px 40px',
    background: '#0b0b0d',
    color: '#f5f5f7',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: '24px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    margin: 0
  },
  subtitle: {
    marginTop: '8px',
    marginBottom: 0,
    color: '#9ca3af',
    maxWidth: '640px',
    lineHeight: 1.4
  },
  main: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)',
    gap: '24px',
    alignItems: 'flex-start'
  },
  card: {
    background: 'rgba(17, 24, 39, 0.9)',
    borderRadius: '18px',
    padding: '20px 24px',
    boxShadow: '0 18px 40px rgba(0, 0, 0, 0.55)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    backdropFilter: 'blur(18px)'
  },
  smallCard: {
    background: 'rgba(17, 24, 39, 0.9)',
    borderRadius: '18px',
    padding: '18px 20px',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.55)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    backdropFilter: 'blur(18px)',
    alignSelf: 'stretch'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    letterSpacing: '-0.01em'
  },
  cardSubtitle: {
    margin: '6px 0 0',
    fontSize: '14px',
    color: '#9ca3af',
    lineHeight: 1.4
  },
  tableWrapper: {
    marginTop: '10px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(55, 65, 81, 0.9)',
    background:
      'linear-gradient(135deg, rgba(15,23,42,0.95), rgba(17,24,39,0.98))'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(55, 65, 81, 0.9)',
    color: '#9ca3af',
    fontWeight: 500
  },
  thCenter: {
    textAlign: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(55, 65, 81, 0.9)',
    color: '#9ca3af',
    fontWeight: 500
  },
  tr: {
    borderBottom: '1px solid rgba(31, 41, 55, 0.9)'
  },
  td: {
    padding: '10px 14px'
  },
  tdCenter: {
    padding: '10px 14px',
    textAlign: 'center'
  },
  warningBox: {
    padding: '10px 14px',
    background: 'rgba(127, 29, 29, 0.3)',
    color: '#fecaca',
    fontSize: '13px'
  },
  warningTitle: {
    fontWeight: 600
  },
  infoBox: {
    padding: '8px 12px',
    fontSize: '13px',
    color: '#e5e7eb',
    background: 'rgba(31, 41, 55, 0.9)',
    borderBottom: '1px solid rgba(55, 65, 81, 0.9)'
  },
  errorBox: {
    padding: '8px 12px',
    fontSize: '13px',
    color: '#fecaca',
    background: 'rgba(127, 29, 29, 0.4)',
    borderBottom: '1px solid rgba(127, 29, 29, 0.7)'
  },
  switchLabel: {
    position: 'relative',
    display: 'inline-block',
    width: '44px',
    height: '24px',
    cursor: 'pointer'
  },
  switchInput: {
    opacity: 0,
    width: 0,
    height: 0
  },
  switchSlider: {
    position: 'absolute',
    inset: 0,
    borderRadius: '999px',
    background: '#374151',
    boxShadow: '0 0 0 1px rgba(15,23,42,0.6)',
    transition: 'background 0.18s ease, box-shadow 0.18s ease'
  },
  switchSliderOn: {
    background: '#22c55e',
    boxShadow: '0 0 0 1px rgba(34,197,94,0.9)'
  },
  primaryButton: {
    marginTop: '12px',
    width: '100%',
    padding: '10px 14px',
    borderRadius: '999px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    background: '#22c55e',
    color: '#052e16',
    cursor: 'pointer',
    transition: 'transform 0.08s ease, box-shadow 0.12s ease, opacity 0.12s ease'
  },
  primaryButtonDisabled: {
    marginTop: '12px',
    width: '100%',
    padding: '10px 14px',
    borderRadius: '999px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    background: 'rgba(55,65,81,0.9)',
    color: '#9ca3af',
    cursor: 'not-allowed',
    opacity: 0.8
  },
  testStatus: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#e5e7eb'
  },
  testHint: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#9ca3af'
  }
};

export default HrMenteeAlertSettings;
